import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import {
  fetchApprovals,
  fetchBooking,
  fetchBookingPaymentSchedule,
  fetchBookingProposal,
  fetchBookingTimeline,
  fetchBookingRefunds,
  fetchCatalogItems,
  fetchCommercialRules,
  fetchEventsBookings,
  fetchEventsOrganizations,
  fetchEventsSession,
  fetchLeads,
  fetchPipeline,
  fetchVenueSettings,
  fetchVenueSpaces,
  previewProposalVersion,
  quoteLines,
} from '../client';
import { eventsKeys } from '../queryKeys';
import {
  type ApprovalState,
  EventsApiError,
  type EventsBookingFilters,
  type EventsScope,
  type ProposalLineInput,
} from './types';

export const retryUnlessClientError = (failureCount: number, error: Error) =>
  !(
    error instanceof EventsApiError &&
    error.status !== null &&
    error.status >= 400 &&
    error.status < 500
  ) && failureCount < 2;

export function useEventsOrganizations({ enabled = true } = {}) {
  return useQuery({
    queryKey: eventsKeys.organizations(),
    queryFn: fetchEventsOrganizations,
    staleTime: 60_000,
    retry: retryUnlessClientError,
    enabled,
  });
}

/**
 * Always stale: every scope selection, including a return to a cached scope,
 * refetches. Callers must gate on isFetchedAfterMount so cached permissions
 * are never rendered for a newly selected scope.
 */
export function useEventsSessionQuery(
  scope: EventsScope,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.session(scope),
    queryFn: () => fetchEventsSession(scope),
    staleTime: 0,
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useEventsBookingsQuery(
  scope: EventsScope,
  filters: EventsBookingFilters = {},
) {
  return useQuery({
    queryKey: eventsKeys.bookings(scope, filters),
    queryFn: () => fetchEventsBookings(scope, filters),
    staleTime: 30_000,
    retry: retryUnlessClientError,
  });
}

export function useLeadsQuery(scope: EventsScope) {
  return useQuery({
    queryKey: eventsKeys.leads(scope),
    queryFn: () => fetchLeads(scope),
    staleTime: 30_000,
    retry: retryUnlessClientError,
  });
}

export function usePipelineQuery(scope: EventsScope) {
  return useQuery({
    queryKey: eventsKeys.pipeline(scope),
    queryFn: () => fetchPipeline(scope),
    staleTime: 30_000,
    retry: retryUnlessClientError,
  });
}

export function useVenueSpacesQuery(
  scope: EventsScope,
  venueId: number | null,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.spaces(scope, venueId ?? 0),
    queryFn: () => fetchVenueSpaces(scope, venueId as number),
    staleTime: 60_000,
    retry: retryUnlessClientError,
    enabled: enabled && venueId !== null,
  });
}

/** Payment schedule and refunds for each booking, keyed per booking. */
export function useBookingPaymentsQueries(
  scope: EventsScope,
  bookingIds: number[],
) {
  const schedules = useQueries({
    queries: bookingIds.map((bookingId) => ({
      queryKey: eventsKeys.paymentSchedule(scope, bookingId),
      queryFn: () => fetchBookingPaymentSchedule(scope, bookingId),
      staleTime: 30_000,
      retry: retryUnlessClientError,
    })),
  });
  const refunds = useQueries({
    queries: bookingIds.map((bookingId) => ({
      queryKey: eventsKeys.refunds(scope, bookingId),
      queryFn: () => fetchBookingRefunds(scope, bookingId),
      staleTime: 30_000,
      retry: retryUnlessClientError,
    })),
  });
  return { schedules, refunds };
}

export function useBookingQuery(scope: EventsScope, bookingId: number) {
  return useQuery({
    queryKey: eventsKeys.booking(scope, bookingId),
    queryFn: () => fetchBooking(scope, bookingId),
    staleTime: 15_000,
    retry: retryUnlessClientError,
  });
}

export function useBookingTimelineQuery(
  scope: EventsScope,
  bookingId: number,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.timeline(scope, bookingId),
    queryFn: () => fetchBookingTimeline(scope, bookingId),
    staleTime: 15_000,
    retry: retryUnlessClientError,
    enabled,
  });
}

/** `scope` is the booking's venue scope (see client.ts). */
export function useBookingProposalQuery(
  scope: EventsScope,
  bookingId: number,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.proposal(scope, bookingId),
    queryFn: () => fetchBookingProposal(scope, bookingId),
    staleTime: 15_000,
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useCatalogItemsQuery(
  scope: EventsScope,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.catalog(scope),
    queryFn: () => fetchCatalogItems(scope),
    staleTime: 60_000,
    retry: retryUnlessClientError,
    enabled,
  });
}

/** Keeps the last quote on screen while the next one loads. */
export function useQuoteQuery(
  scope: EventsScope,
  lines: ProposalLineInput[],
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.quote(scope, lines),
    queryFn: () => quoteLines(scope, lines),
    staleTime: 30_000,
    retry: retryUnlessClientError,
    placeholderData: keepPreviousData,
    enabled: enabled && lines.length > 0,
  });
}

export function useProposalPreviewQuery(
  scope: EventsScope,
  versionId: number,
  contentHash: string,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.preview(scope, versionId, contentHash),
    queryFn: () => previewProposalVersion(scope, versionId),
    staleTime: Infinity,
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useApprovalsQuery(
  scope: EventsScope,
  state?: ApprovalState,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.approvals(scope, state),
    queryFn: () => fetchApprovals(scope, state),
    staleTime: 15_000,
    retry: retryUnlessClientError,
    enabled,
  });
}

export function useVenueSettingsQuery(scope: EventsScope, venueId: number) {
  return useQuery({
    queryKey: eventsKeys.venueSettings(scope, venueId),
    queryFn: () => fetchVenueSettings(scope, venueId),
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

/** 404 until the venue's fee and deposit rules exist. */
export function useCommercialRulesQuery(scope: EventsScope) {
  return useQuery({
    queryKey: eventsKeys.rules(scope),
    queryFn: () => fetchCommercialRules(scope),
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

export function useBookingRefundsQuery(
  scope: EventsScope,
  bookingId: number,
  { enabled = true } = {},
) {
  return useQuery({
    queryKey: eventsKeys.refunds(scope, bookingId),
    queryFn: () => fetchBookingRefunds(scope, bookingId),
    staleTime: 15_000,
    retry: retryUnlessClientError,
    enabled,
  });
}
